// client/src/components/TableManager.jsx
import React, { useEffect, useState, useContext } from 'react';
import api from '../utils/api';
import { ProjectContext } from '../contexts/ProjectContext';

export default function TableManager({ onLoad, onShare, onUnshare, projectId: pidProp }) {
  const { activeProject } = useContext(ProjectContext);
  const projectId = pidProp || activeProject?._id;

  const [tables, setTables]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [busyId,  setBusyId]  = useState(null); // disable row while sharing/deleting

  // Load list of tables when project changes
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/projects/${projectId}/tables`)
      .then(res => {
        setTables(res.data || []);
        setError(null);
      })
      .catch(err => {
        console.error('[TableManager] fetch error', err);
        setError('Failed to load saved tables');
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  const handleLoad = (table) => onLoad?.(table);

  const handleDelete = async (tableId) => {
    if (!window.confirm('Delete this saved estimate?')) return;
    setBusyId(tableId);
    try {
      await api.delete(`/projects/${projectId}/tables/${tableId}`);
      setTables(prev => prev.filter(t => t._id !== tableId));
    } catch (err) {
      console.error('[TableManager] delete error', err);
      alert(err?.response?.data?.error || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  // Internal fallback call if parent didn't pass onShare/onUnshare
  const callShareApi = async (fileName, shared) => {
    await api.post('/estimates/share-by-filename', {
      fileName,        // e.g. "My_Estimate.csv"
      projectId,       // helps resolver find the object
      shared           // true share, false unshare
    });
  };

  const share = async (row, shared) => {
    if (!row?.label) return;
    setBusyId(row._id);
    try {
      if (shared) {
        if (onShare) await onShare(row.label);
        else await callShareApi(row.label, true);
      } else {
        if (onUnshare) await onUnshare(row.label);
        else await callShareApi(row.label, false);
      }
      // No local state change needed; we keep the list as-is.
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to change sharing.';
      console.error('[TableManager] share/unshare error', msg);
      alert(msg);
    } finally {
      setBusyId(null);
    }
  };

  if (!projectId) {
    return <p className="text-zinc-400">Select a project to see saved tables.</p>;
  }

  // style tokens
  const card = 'glass-card';
  const btnOutline =
    'glass-ghost text-sm px-3 py-2 disabled:opacity-60';
  const btnDanger =
    'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold ' +
    'text-red-100 border border-red-400/40 bg-red-500/20 hover:bg-red-500/30 transition disabled:opacity-60';
  const thead = 'bg-white/5 text-slate-400 text-xs uppercase tracking-wide';
  const cell = 'px-4 py-3';

  return (
    <div className={`${card} p-6`}>
      <h3 className="mb-4 text-lg font-semibold text-amber-300">Saved Estimates</h3>

      {loading && <p className="text-zinc-400">Loading…</p>}
      {error && <p className="text-sm text-red-300">{error}</p>}
      {!loading && !error && tables.length === 0 && (
        <p className="text-zinc-500">No saved estimates yet.</p>
      )}

      {!loading && !error && tables.length > 0 && (
        <div className="glass-panel rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm text-zinc-200">
            <thead className={thead}>
              <tr>
                <th className={`${cell} text-left`}>Label</th>
                <th className={`${cell}`}>Total Cost</th>
                <th className={`${cell}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tables.map((t) => {
                const createdAt = new Date(Number(t._id)).toLocaleString();
                const busy = busyId === t._id;
                return (
                  <tr key={t._id} className="border-t border-white/10 hover:bg-white/5 transition">
                    {/* Label + created at */}
                    <td className={cell}>
                      <div className="max-w-[28rem] truncate text-zinc-100">{t.label}</div>
                      <div className="mt-0.5 text-xs text-zinc-500">{createdAt}</div>
                    </td>

                    <td className={`${cell} whitespace-nowrap`}>
                      ${Number(t.totalCost || 0).toFixed(2)}
                    </td>

                    <td className={cell}>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => handleLoad(t)} className={btnOutline} disabled={busy}>
                          {busy ? 'Please wait…' : 'Load'}
                        </button>
                        <button onClick={() => share(t, true)} className={btnOutline} disabled={busy}>
                          Share
                        </button>
                        <button onClick={() => share(t, false)} className={btnOutline} disabled={busy}>
                          Unshare
                        </button>
                        <button onClick={() => handleDelete(t._id)} className={btnDanger} disabled={busy}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
